import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  IconButton,
} from '@buffer-mono/popcorn'

import styles from './PaginationControls.module.css'

import type { JSX } from 'react'

interface PaginationControlsProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  loading?: boolean
}

/**
 * Build the list of page numbers (and ellipsis gaps) to display.
 *
 * - Always show first and last page
 * - Show pages around the current page
 * - Insert '...' for gaps
 * - When totalPages <= 7, show all pages
 */
export function getPageRange(
  current: number,
  total: number,
): Array<number | 'ellipsis'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages: Array<number | 'ellipsis'> = []

  pages.push(1)

  if (current <= 4) {
    for (let i = 2; i <= 5; i++) pages.push(i)
    pages.push('ellipsis')
    pages.push(total)
  } else if (current >= total - 3) {
    pages.push('ellipsis')
    for (let i = total - 4; i <= total; i++) pages.push(i)
  } else {
    pages.push('ellipsis')
    pages.push(current - 1)
    pages.push(current)
    pages.push(current + 1)
    pages.push('ellipsis')
    pages.push(total)
  }

  return pages
}

export function PaginationControls({
  page,
  totalPages,
  onPageChange,
  loading,
}: PaginationControlsProps): JSX.Element {
  const pages = getPageRange(page, totalPages)

  return (
    <nav aria-label="Pagination" className={styles.container}>
      <IconButton
        label="Previous page"
        variant="tertiary"
        size="small"
        disabled={page <= 1 || loading}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeftIcon />
      </IconButton>

      {pages.map((item, index) =>
        item === 'ellipsis' ? (
          <Button
            // eslint-disable-next-line react/no-array-index-key
            key={`ellipsis-${index}`}
            variant="tertiary"
            size="small"
            disabled
            className={styles.ellipsis}
          >
            &hellip;
          </Button>
        ) : (
          <Button
            key={item}
            variant="tertiary"
            size="small"
            className={item === page ? styles.active : styles.pageNumber}
            onClick={() => {
              if (item !== page) onPageChange(item)
            }}
            disabled={loading}
            aria-label={`Page ${item}`}
            aria-current={item === page ? 'page' : undefined}
          >
            {item}
          </Button>
        ),
      )}

      <IconButton
        label="Next page"
        variant="tertiary"
        size="small"
        disabled={page >= totalPages || loading}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRightIcon />
      </IconButton>
    </nav>
  )
}
